import * as thermostat from './sources/thermostat';
import * as gtw from './sources/gtw';
import * as ztc from './sources/ztc';
import * as opentherm from './sources/opentherm';
import * as cc from './sources/customcontrols';
import * as rf from './sources/rf';
import * as z3k from './sources/z3k';
import { show_temp_for_rf_type } from 'console/tabs/legacy/rf/rf-utils';
import { show_sim_balance } from 'console/tabs/legacy/state/components/cards/gsm/show-sim-balance';
import 'webui/mui/dialog';
import 'webui/mui/checkbox';
import { getDeviceSpec } from 'z3k-parser/model/device-spec/z3k-feature-flags';
import { AnalogInput, AnalogOutput, WebElement } from 'z3k-config';
import { Capability } from 'console/device/device-capability';
import { removeNulls } from 'utils';
class GraphSourceGroup {
    title(device) {
        return '??';
    }
    collapsible() {
        return false;
    }
    static available(device) {
        return false;
    }
    possible_sources(device) {
        return [];
    }
    mandatory_sources(device) {
        return [];
    }
    default_sources(device) {
        return [...this.mandatory_sources(device), ...this.possible_sources(device)];
    }
}
export class TemperatureSensorsGraphSourceGroup extends GraphSourceGroup {
    title() {
        return 'Датчики температуры';
    }
    static available(device) {
        return device.has_capability(Capability.has_multiple_thermometers);
    }
    therm_specs(device) {
        return device.active_thermometers();
    }
    sort_therm_specs(therm_specs) {
        return therm_specs.sort((a, b) => {
            if (a.sort < b.sort)
                return -1;
            if (a.sort > b.sort)
                return +1;
            return 0;
        });
    }
    // graph_for_therm_spec: (device, spec) -> switch spec.type
    //     when 'radio' and device.supports_rf_graphs() then new rf.RFTemperatureGraphSource device, serial: spec.serial
    //     else new thermostat.TemperatureSensorGraphData device, therm_id: spec.uuid
    graph_for_therm_spec(device, spec) {
        return new thermostat.TemperatureSensorGraphData(device, {
            therm_id: spec.uuid,
        });
    }
    possible_sources(device) {
        const therm_specs = this.sort_therm_specs(this.therm_specs(device));
        const therm_graphs = therm_specs.map((spec) => this.graph_for_therm_spec(device, spec));
        const therm_serials = therm_specs.map((spec) => spec.serial);
        const rf_graphs = Object.entries(device.rf_status() ?? {})
            .filter(([serial, _rf_props]) => !therm_serials.includes(serial))
            .filter(([_serial, rf_props]) => show_temp_for_rf_type(rf_props.type))
            .map(([serial, _rf_props]) => new rf.RFTemperatureGraphSource(device, { serial }));
        return [...therm_graphs, ...rf_graphs];
    }
}
export class DisconnectedSensorsGraphSourceGroup extends GraphSourceGroup {
    title() {
        return 'Отключенные датчики температуры';
    }
    static available(device) {
        return device.has_capability(Capability.has_multiple_thermometers);
    }
    collapsible() {
        return true;
    }
    therm_specs(device) {
        return (device.thermometers() ?? []).filter((therm) => !therm.is_assigned_to_slot);
    }
    possible_sources(device) {
        const therm_specs = this.therm_specs(device);
        therm_specs.sort((a, b) => {
            if (a.sort < b.sort)
                return -1;
            if (a.sort > b.sort)
                return +1;
            return 0;
        });
        return therm_specs.map((spec) => new thermostat.TemperatureSensorGraphData(device, {
            therm_id: spec.uuid,
        }));
    }
}
export class ThermostatGraphSourceGroup extends GraphSourceGroup {
    title() {
        return 'Термостат';
    }
    static available(device) {
        if (device.has_capability(Capability.has_z3k_settings))
            return false;
        else if (device.has_capability(Capability.has_thermostat))
            return true;
        else if (device.has_capability(Capability.has_optional_thermostat) &&
            device.thermostat_enabled())
            return true;
        else
            return false;
    }
    possible_sources(device) {
        const sources = [];
        if (device.thermostat_target_temps() != null) {
            const zone_count = Object.keys(device.thermostat_target_temps() ?? {}).length;
            for (let zone_no = 1; zone_no < zone_count; zone_no++) {
                sources.push(new thermostat.ZoneTargetTemp(device, { zone_no }));
                sources.push(new thermostat.ZoneWorkTime(device, { zone_no }));
                if (device.supports_heatsettemp())
                    sources.push(new thermostat.HeatSetTempGraph(device, { zone_no }));
            }
            if (device.ot_enabled())
                sources.push(new thermostat.DHWTargetTemp(device));
        }
        else {
            sources.push(new thermostat.TargetTempGraphSource(device));
            sources.push(new thermostat.BoilerWorkGraphSource(device, {}));
        }
        if (device.pza()?.enabled) {
            sources.push(new thermostat.PZATempGraphSource(device));
            if (device.use_internet_weather_for_pza())
                sources.push(new thermostat.WeatherGraphSource(device));
        }
        return sources;
    }
    mandatory_sources(device) {
        return [
            new thermostat.ThermostatModeGraphSource(device),
            new thermostat.BoilerFailGraphSource(device),
            new thermostat.NoPowerGraphSource(device),
            new thermostat.ThermostatGateGraphSource(device),
        ];
    }
}
export class GTWGraphSourceGroup extends GraphSourceGroup {
    title() {
        return 'Показания ZONT Expert';
    }
    static available(device) {
        return device.has_capability(Capability.has_gtw_reports);
    }
    possible_sources(device) {
        return [
            new gtw.GTWTAirIntGraphSource(device),
            new gtw.GTWTAirExtGraphSource(device),
            new gtw.GTWTWaterGraphSource(device),
            new gtw.GTWPWaterGraphSource(device),
            new gtw.GTWTenCountGraphSource(device),
        ];
    }
    mandatory_sources(device) {
        return [new gtw.GTWModeGraphSource(device)];
    }
}
export class ZTCTempSourceGroup extends GraphSourceGroup {
    title() {
        return 'Датчики температуры';
    }
    static available(device) {
        return device.has_capability(Capability.has_temperature_sensors);
    }
    possible_sources(device) {
        const sources = [];
        for (let i = 0; i < 5; i++)
            sources.push(new ztc.ZTCTempGraphSource(device, { temp_no: i }));
        return sources;
    }
}
export class ZTCGSMSourceGroup extends GraphSourceGroup {
    title(device) {
        if (device.has_capability(Capability.has_wifi))
            return 'Состояние Wi-Fi';
        else
            return 'Состояние GSM';
    }
    static available(device) {
        return device.supports_ztc_state_graphs();
    }
    possible_sources(device) {
        return removeNulls([
            new ztc.ZTCGSMLevelGraphSource(device),
            show_sim_balance(device.sim_in_device(), device.user_id(), device._ui.store.getState().account)
                ? new ztc.ZTCGSMBalanceGraphSource(device)
                : null,
            new ztc.ZTCGSMState_HomeNetwork(device),
            new ztc.ZTCGSMState_Searching(device),
            new ztc.ZTCGSMState_Rejected(device),
            new ztc.ZTCGSMState_Roaming(device),
            new ztc.ZTCConnectionState_MainPower(device),
            new ztc.ZTCConnectionState_SupplyPower(device),
            new ztc.ZTCConnectionState_GSM(device),
            new ztc.ZTCConnectionState_WiFi(device),
            new ztc.ZTCWiFiLevelGraphSource(device),
            new ztc.ZTCConnectionState_Ethernet(device),
        ]);
    }
    default_sources(device) {
        return [];
    }
}
export class ZTCGPSSourceGroup extends GraphSourceGroup {
    title() {
        return 'Состояние GPS';
    }
    static available(device) {
        return (device.has_capability(Capability.has_gps) &&
            !device.has_capability(Capability.has_autoscan_packets));
    }
    possible_sources(device) {
        return [
            new ztc.ZTCSpeedDataSource(device),
            new ztc.ZTCAltitudeDataSource(device),
            new ztc.ZTCSatellitesDataSource(device),
            new ztc.ZTCHDOPDataSource(device),
            //        new ztc.ZTCAvgVADataSource device
        ];
    }
    default_sources(device) {
        return [new ztc.ZTCSpeedDataSource(device)];
    }
}
export class ZTCVoltageSourceGroup extends GraphSourceGroup {
    title() {
        return 'Питание';
    }
    static available(device) {
        return device.supports_ztc_state_graphs();
    }
    possible_sources(device) {
        return [new ztc.ZTCVoltageGraphSource(device)];
    }
    default_sources(device) {
        return [];
    }
}
export class OTGraphSourceGroup extends GraphSourceGroup {
    title() {
        return 'Цифровая шина';
    }
    static available(device) {
        return device.ot_enabled();
    }
    possible_sources(device) {
        return [
            new opentherm.OTGraphSource_CS(device),
            new opentherm.OTGraphSource_BT(device),
            // new opentherm.OTGraphSource_DS device
            new opentherm.OTGraphSource_DT(device),
            new opentherm.OTGraphSource_RWT(device),
            new opentherm.OTGraphSource_OT(device),
            new opentherm.OTGraphSource_RML(device),
            new opentherm.OTGraphSource_WP(device),
            new opentherm.OTGraphSource_FR(device),
            new opentherm.OTCHFlagGraphSource(device),
            new opentherm.OTDHWFlagGraphSource(device),
        ];
    }
}
export class OTZoneGraphSourceGroup extends GraphSourceGroup {
    zone_no;
    zones_count;
    constructor(zone_no, zones_count) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this.zone_no = zone_no;
        this.zones_count = zones_count;
    }
    title() {
        if (this.zones_count === 1)
            return 'Цифровая шина';
        else
            return `Цифровая шина №${this.zone_no + 1}`;
    }
    possible_sources(device) {
        return [
            ...opentherm.OTZoneLineGraph.all_for_zone(device, {
                zone_no: this.zone_no,
                ot_field: '', /////////////??????????????
            }),
            ...opentherm.OTZoneFlagGraph.all_for_zone(device, {
                zone_no: this.zone_no,
                ot_flag: '', /////////////??????????????
            }),
        ];
    }
}
export class CustomControlsSourceGroup extends GraphSourceGroup {
    title() {
        return 'Пользовательские статусы';
    }
    static available(device) {
        return device.custom_controls()?.length > 0;
    }
    possible_sources(device) {
        return Object.entries(device.custom_controls() ?? [])
            .filter(([_i, control]) => control.statuses?.[0].id != null)
            .map(([i, _control]) => new cc.CustomControlStatusGraphSource(device, {
            control_no: ~~i,
        }));
    }
}
export class HumidityGroup extends GraphSourceGroup {
    title() {
        return 'Влажность воздуха';
    }
    static available(device) {
        if (!device.supports_rf_graphs())
            return false;
        for (const rf_props of Object.values(device.rf_status() ?? {}))
            if (rf_props?.hum != null)
                return true;
        return false;
    }
    possible_sources(device) {
        return Object.entries(device.rf_status() ?? {})
            .filter(([_serial, rf_props]) => rf_props?.hum != null)
            .map(([serial, _rf_props]) => new rf.RFHumidityGraph(device, { serial }));
    }
}
export class RFSourceGroup extends GraphSourceGroup {
    title() {
        return 'Радиоустройства';
    }
    static available(device) {
        return device.rf_status() != null && device.supports_rf_graphs();
    }
    collapsible() {
        return true;
    }
    possible_sources(device) {
        const battery_graphs = Object.keys(device.rf_status() ?? {}).map((serial) => new rf.RFBatteryGraph(device, { serial }));
        const signal_graphs = Object.keys(device.rf_status() ?? {}).map((serial) => new rf.RFSignalGraph(device, { serial }));
        return [...battery_graphs, ...signal_graphs];
    }
    default_sources() {
        return [];
    }
}
export class Z3KTemperatureGroup extends GraphSourceGroup {
    title() {
        return 'Проводные термодатчики';
    }
    static available(device) {
        return device.has_capability(Capability.has_z3k_settings);
    }
    possible_sources(device) {
        const wired = (device.z3k_config()?.wired_temperature_sensors ?? []).map((obj) => new z3k.WiredTemperatureGraphData(device, { object_id: obj.id }));
        const analog = (device.z3k_config()?.analog_temperature_sensors ?? []).map((obj) => new z3k.AnalogTemperatureGraphData(device, { object_id: obj.id }));
        return [...wired, ...analog];
    }
}
export class Z3KBoilerAdaptersGroup extends GraphSourceGroup {
    object;
    constructor(object) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this.object = object;
    }
    title() {
        return this.object.name ?? `Адаптер котла №${this.object.id}`;
    }
    static available(device) {
        return device.has_capability(Capability.has_z3k_settings);
    }
    possible_sources(device) {
        return [
            ...z3k.Z3KOTZoneLineGraph.all_for_object_id(device, this.object.id),
            ...z3k.Z3KOTZoneFlagGraph.all_for_object_id(device, this.object.id),
            new z3k.Z3KOTFailGraph(device, { object_id: this.object.id }),
        ];
    }
}
export class Z3KRadioSensorsGroup extends GraphSourceGroup {
    title() {
        return 'Радиодатчики';
    }
    static available(device) {
        return device.has_capability(Capability.has_z3k_settings);
    }
    possible_sources(device) {
        return (device.z3k_config()?.radiosensors ?? []).flatMap((obj) => z3k.create_z3k_radio_sources(device, obj));
    }
}
export class Z3KHeatingCircuitGroup extends GraphSourceGroup {
    title() {
        return 'Целевая температура';
    }
    static available(device) {
        return device.has_capability(Capability.has_z3k_settings);
    }
    possible_sources(device) {
        const heating = getDeviceSpec(device.device_type()?.code, device.hardware_type()?.code).heating;
        //@ts-ignore
        return (device.z3k_config()?.heating_circuits ?? []).flatMap((obj) => removeNulls([
            new z3k.HeatingCircuitSetpointTempGraphData(device, {
                object_id: obj.id,
            }),
            obj.type !== 0
                ? new z3k.HeatingCircuitTargetTempGraphData(device, {
                    object_id: obj.id,
                })
                : null,
            heating.has_heating && heating.show_heating_circuit_worktime_graphs
                ? new z3k.HeatingCircuitWorkTimeGraphData(device, {
                    object_id: obj.id,
                })
                : null,
            new z3k.HeatingCircuitSummerModeGraphData(device, { object_id: obj.id }),
        ]));
    }
}
export class Z3KAnalogInputGroup extends GraphSourceGroup {
    title() {
        return 'Напряжение, сопротивление и давление';
    }
    static available(device) {
        return device.has_capability(Capability.has_z3k_settings);
    }
    possible_sources(device) {
        return (device.z3k_config()?.analog_inputs ?? []).flatMap((obj) => {
            if (AnalogInput.isPressureInput(obj))
                return [
                    new z3k.AnalogInputPressureGraphData(device, {
                        object_id: obj.id,
                    }),
                ];
            if (obj.sensor_type === AnalogInput.Type.IGNITION ||
                (AnalogInput.isAnalogInputType(obj) &&
                    !AnalogInput.isUseResistance(obj)))
                return [
                    new z3k.AnalogInputVoltageGraphData(device, {
                        object_id: obj.id,
                    }),
                ];
            if (AnalogInput.isAnalogInputType(obj) && AnalogInput.isUseResistance(obj))
                return [
                    new z3k.AnalogInputResistanceGraphData(device, {
                        object_id: obj.id,
                    }),
                ];
            if (obj.sensor_type === AnalogInput.Type.SPEED_SENSOR)
                return [
                    new z3k.AnalogInputSpeedGraphData(device, {
                        object_id: obj.id,
                    }),
                ];
            if (obj.sensor_type === AnalogInput.Type.RPM_SENSOR)
                return [
                    new z3k.AnalogInputTurnGraphData(device, {
                        object_id: obj.id,
                    }),
                ];
            if (obj.sensor_type === AnalogInput.Type.DISCRETE)
                return [
                    new z3k.AnalogInputDiscreteGraphData(device, {
                        object_id: obj.id,
                    }),
                ];
            if (obj.sensor_type === AnalogInput.Type.ALARM_BUTTON)
                return [
                    new z3k.AnalogInputAlarmButtonGraphData(device, {
                        object_id: obj.id,
                    }),
                ];
            if (obj.sensor_type === AnalogInput.Type.FUEL_STAT_SENSOR)
                return [
                    new z3k.AnalogInputFuelStatGraphData(device, {
                        object_id: obj.id,
                    }),
                ];
            if (obj.sensor_type === AnalogInput.Type.HUMIDITY_SENSOR)
                return [
                    new z3k.AnalogInputHumidityGraphData(device, {
                        object_id: obj.id,
                    }),
                ];
            return [];
        });
    }
}
export class Z3KWebElementGroup extends GraphSourceGroup {
    title() {
        return 'Состояние';
    }
    static available(device) {
        return device.has_capability(Capability.has_z3k_settings);
    }
    possible_sources(device) {
        return (device.z3k_config()?.web_elements ?? []).map((obj) => {
            const analog_output = AnalogOutput.get.byId(device.z3k_config(), 
            //@ts-ignore
            WebElement.get.byId(device.z3k_config(), obj.id)?.id_active);
            if (!analog_output) {
                return new z3k.WebElementStateBooleanGraphData(device, {
                    object_id: obj.id,
                });
            }
            return new z3k.WebElementStateGraphData(device, { object_id: obj.id });
        });
    }
}
export const all_default_graph_sources = (device) => {
    return groups_for_device(device).flatMap((group) => group.default_sources(device));
};
///////////////////////////////////////////////////////////////////////
// export const GraphDialog = {
//     all_default_graph_sources(device: LegacyDevice) {
//         return groups_for_device(device).flatMap((group) =>
//             group.default_sources(device)
//         );
//     },
//     show(device: LegacyDevice, block: GraphBlock | null = null) {
//         const result = $.Deferred();
//         const checked = (source: Classes) => {
//             if (block == null) {
//                 return false;
//             }
//             return block
//                 .sources()
//                 .some((existing: Classes) => source.is_same(existing));
//         };
//         const groups = groups_for_device(device);
//         const psources = groups.map((group) => group.possible_sources(device));
//         //psources -> mas[][]
//         const html = Object.entries(groups)
//             //@ts-ignore
//             .filter(([group_no, group]) => psources[group_no].length > 0)
//             .map(([group_no, group]) => {
//                 //@ts-ignore
//                 const checkboxes = psources[group_no].map(
//                     (source: Classes, i: number) =>
//                         `<div class="ui-graph-dialog-source" psource_no="${i}">
//                         <span class="mui_checkbox ${
//                             checked(source) ? 'mui_checkbox_checked' : ''
//                         }">
//                             <span class="mui_checkbox_icon"></span>
//                             ${escape_html(source.title())}
//                         </span>
//                     </div>`
//                 );
//                 return `<div group_no="${group_no}"
//                      class='ui-graph-dialog-source-group ${
//                          group.collapsible()
//                              ? 'ui-graph-dialog-source-group-collapsible'
//                              : ''
//                      }'>
//                     <h2>${escape_html(group.title(device))}</h2>
//                     <div class='ui-graph-dialog-sources'>
//                         ${checkboxes.join('')}
//                     </div>
//                 </div>`;
//             });
//         const mandatory = groups.flatMap((group) => group.mandatory_sources(device));
//         const dialog = $(
//             `<div class="ui-graphs-tab-sources-dialog">${html.join('')}</div>`
//         ).on('click', '.ui-graph-dialog-source-group-collapsible h2', function () {
//             return $(this)
//                 .closest('.ui-graph-dialog-source-group-collapsible')
//                 .toggleClass('expanded');
//         });
//         let close: () => void;
//         ({close} = nonreact_messagebox({
//             size: 'large',
//             title: 'Выберите графики',
//             dom_element: (div) => {
//                 if (div != null) {
//                     dialog.appendTo(div);
//                 } else dialog.detach();
//             },
//             buttons_in_footer: true,
//             message: '',
//             buttons: [
//                 {
//                     text: 'OK',
//                     click: () => {
//                         const selected_sources = dialog
//                             .find('.mui_checkbox_checked')
//                             .map((i, checkbox) => {
//                                 const p_source_no = $(checkbox)
//                                     .closest('[psource_no]')
//                                     .attr('psource_no');
//                                 const group_no = $(checkbox)
//                                     .closest('[group_no]')
//                                     .attr('group_no');
//                                 //@ts-ignore
//                                 return psources[group_no][p_source_no];
//                             })
//                             .get();
//                         selected_sources.push(...mandatory);
//                         result.resolve(selected_sources);
//                         close();
//                     },
//                 },
//             ],
//         }));
//         return result.promise();
//     },
// };
///////////////////////////////////////////////////////////////////////
export const groups_for_device = (device) => {
    const simple_groups = removeNulls([
        GTWGraphSourceGroup,
        ThermostatGraphSourceGroup,
        TemperatureSensorsGraphSourceGroup,
        HumidityGroup,
        ZTCTempSourceGroup,
        ZTCGPSSourceGroup,
        device.has_any_capability([Capability.has_gsm, Capability.has_wifi])
            ? ZTCGSMSourceGroup
            : null,
        ZTCVoltageSourceGroup,
        CustomControlsSourceGroup,
        RFSourceGroup,
        Z3KTemperatureGroup,
        Z3KRadioSensorsGroup,
        Z3KHeatingCircuitGroup,
        Z3KAnalogInputGroup,
        Z3KWebElementGroup,
    ]);
    const groups = simple_groups
        .filter((group) => group.available(device))
        .map((group) => new group());
    let ot_groups;
    if (device.ot_enabled()) {
        if (device.supports_otzones_packets()) {
            const zones_count = device.ot_zones_count();
            ot_groups = new Array(zones_count)
                .fill(0)
                .map((_, zone_no) => new OTZoneGraphSourceGroup(zone_no, zones_count));
        }
        else
            ot_groups = [new OTGraphSourceGroup()];
    }
    else
        ot_groups = [];
    groups.push(...ot_groups);
    groups.push(...(device.z3k_config()?.boiler_adapters ?? []).map((obj) => new Z3KBoilerAdaptersGroup(obj)));
    groups.push(new DisconnectedSensorsGraphSourceGroup());
    return groups;
};
